import React, { useEffect, useState, useContext, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { App, Form, Select, Button, TimePicker, Input, Alert, Col, Row, Typography, Divider } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import { useIntl } from 'react-intl'
import dayjs from 'dayjs'
import localizedFormat from 'dayjs/plugin/localizedFormat'

dayjs.extend(localizedFormat)

import isEmpty from 'lodash/isEmpty'
import { useManualQuery } from 'graphql-hooks'

import Api from '@vacationtracker/shared/services/api'
import {
  getLocationsApproverTeamsAndLabels,
  getLocationsTeamsAndLabelsShort,
  getNotificationById,
  getUsersFilteredByStatus,
  getUsersForNotificationsWithBirthdayDate,
} from '../../../graphql/custom-queries'

import * as logger from '../../../services/logger'
import { notificationStore } from '../../../context/notificationsContext/store'
import { useShouldEnableFeatures } from '../../../store/use-should-enable-features'
import { useAppSelector } from '../../../store/hooks'
import { selectAuthUserSlice } from '../../../store/auth-user-slice'
import { selectAuthCompanySlice } from '../../../store/auth-company-slice'
import { filterOptions } from '../../../util/filter-options'
import { getNotificationTimezoneFromDraftOrDefault, resolveNotificationTimezone } from '../../../util/notification-timezone'
import { redirectIfEntityNotFound } from '../../../util/entity-not-found-redirect'
import timezone from '@vacationtracker/shared/data/timezone'

import CircularProgress from '../../../components/circular-progress'
import NotificationDestinations from '../../../components/notification-destination'
import IntlMessages from '@vacationtracker/shared/components/utils/IntlMessages'
import { FrontendUrls } from '../../../types/urls'
import { PageHeader } from '../../../design-system/PageHeader'
import FilterAdvanced from '@vacationtracker/shared/components/filter-advanced'
import { availableLanguages } from '@vacationtracker/shared/i18n'

import { INotificationForm, INotificationsPage, IDestination, IDestinationForm, IGetNotificationById } from '../../../types/notifications'
import {
  IGetLocationsApproverTeamsAndLabelsShort,
  IGetLocationsTeamsAndLabelsShort,
  IGetUsersFilteredByStatusResponse,
  IGetUsersForBirthdayNotifications,
  IUserIdAndName,
} from '../../../types/custom-queries'
import { IGetLabelsShort } from '../../../types/labels'
import { IFilter } from '@vacationtracker/shared/types/filter'
import { ITeamShort } from '@vacationtracker/shared/types/team'
import { ILocationShort } from '@vacationtracker/shared/types/location'
import { ISelected } from '@vacationtracker/shared/components/filter-advanced/types'
import { NotificationPlatform } from '@vacationtracker/shared/types/notification-v2'
import { LocaleEnum } from '@vacationtracker/shared/types/i18n'
import { SubscriptionPlanEnum } from '@vacationtracker/shared/types/company'
import { FeatureFlagEnum } from '@vacationtracker/shared/types/feature-flags'
import { IGetTeamsShort } from '../../../types/teams'
import { IGetLocationsShort } from '../../../types/locations'
import { HourFormatEnum } from '@vacationtracker/shared/types/user'
import YesNoSwitch from '../../../components/yes-no-switch'
import BirthdayMessagePreview from '../../../components/birthday-message-preview'
import BirthdaySummaryInfo from '../../../components/birthday-summary-info'

export interface ISelectedDestination {
  type: string
  values: string | string[]
}

const { Title, Paragraph, Text } = Typography

const defaultNotificationState = {
  id: '',
  name: '',
  frequency: 'BIRTHDAY',
  hour: 10,
  minute: Number('00'),
  timezone: '',
  enabled: true,
  locations: [],
  teams: [],
  labels: [],
  locale: LocaleEnum.en,
  sendOnNonWorkingDays: false,
  owner: {
    id: '',
    name: '',
  },
}

const NotificationsBirthdayFormPage = ({ match }: INotificationsPage): React.ReactElement => {
  const [form] = Form.useForm()
  const { notification: antdNotification } = App.useApp()

  const { authCompany } = useAppSelector(selectAuthCompanySlice)
  const {
    authUser: { id, role, locale, hourFormat, name: authUserName, location: authUserLocation },
  } = useAppSelector(selectAuthUserSlice)
  const userTimezonePreference = authUserLocation?.timezone
  const history = useHistory()
  const { formatMessage } = useIntl()
  const { actionNotifications, setActionNotifications } = useContext(notificationStore)

  const [isLoading, setIsLoading] = useState(true)
  const [destinationSelected, setDestinationSelected] = useState<IDestination[]>([])
  const [applyToAllUsers, setApplyToAllUsers] = useState<boolean>(true)
  const [sendOnNonWorkingDays, setSendOnNonWorkingDays] = useState<boolean>(true)
  const [users, setUsers] = useState<IUserIdAndName[]>([])
  const [isLoadingNotification, setIsLoadingNotification] = useState(true)
  const [isApiInProgress, setIsApiInProgress] = useState(false)
  const [channelName, setChannelName] = useState('')
  const [teams, setTeams] = useState<ITeamShort[]>([])
  const [locations, setLocations] = useState<ILocationShort[]>([])
  const [totalUsers, setTotalUsers] = useState<number>(0)
  const [totalUsersWithBirthdayDate, setTotalUsersWithBirthdayDate] = useState<number>(0)
  const [labels, setLabels] = useState<IGetLabelsShort[]>([])
  const [initialFilterValues, setInitialFilterValues] = useState([{}])
  const [initialDestination, setInitialDestination] = useState<ISelectedDestination[] | [{}]>([{}])
  const [shouldResetFilters, setShouldResetFilters] = useState(false)
  const [shouldResetNotificationDestination, setShouldResetNotificationDestination] = useState(false)
  const [filter, setFilter] = useState<IFilter>({
    locationIds: [],
    teamIds: [],
    labelIds: [],
  })
  const [notification, setNotification] = useState<INotificationForm>(defaultNotificationState as INotificationForm)
  const isUpadateNotification = !!match.params.id
  const previewLocale = Form.useWatch<LocaleEnum | undefined>('locale', form)

  const abortControllerRef = useRef<AbortController>()

  const shouldEnableLabels = useShouldEnableFeatures(SubscriptionPlanEnum.complete, FeatureFlagEnum.labels)
  const amIAdmin = role.toLowerCase() === 'admin'
  // TODO: if there are no user show meaningful message
  const amIApprover = role.toLowerCase() === 'approver'

  const [getLocationsTeamsAndLabelsShortQuery] = useManualQuery<IGetLocationsTeamsAndLabelsShort>(getLocationsTeamsAndLabelsShort)
  const [getLocationsApproverTeamsAndLabelsQuery] = useManualQuery<IGetLocationsApproverTeamsAndLabelsShort>(getLocationsApproverTeamsAndLabels)
  const [getNotificationByIdQuery] = useManualQuery<IGetNotificationById, { id: string }>(getNotificationById)
  const [getUsersFilteredByStatusQuery] = useManualQuery<IGetUsersFilteredByStatusResponse, { status: string }>(getUsersFilteredByStatus)
  const [getUsersForNotifictionsQuery] = useManualQuery<
    IGetUsersForBirthdayNotifications,
    {
      locationIds: string[]
      teamIds: string[]
      labelIds: string[]
    }
  >(getUsersForNotificationsWithBirthdayDate)

  useEffect(() => {
    abortControllerRef.current = new AbortController()
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  useEffect(() => {
    if (isUpadateNotification && isLoadingNotification) {
      fetchNotification(match.params.id)
    } else {
      const notificationForm = localStorage.getItem('notificationBirthdayForm')
      if (notificationForm) {
        const notificationFormObject = JSON.parse(notificationForm)
        const initialTz = getNotificationTimezoneFromDraftOrDefault(notificationFormObject.timezone, userTimezonePreference)
        form.setFieldsValue({ ...notificationFormObject, timezone: initialTz })
        handleSetInitialDestinationFromStorage(notificationFormObject)
      } else {
        const initialTz = resolveNotificationTimezone(userTimezonePreference)
        const draft = {
          ...defaultNotificationState,
          locale,
          timezone: initialTz,
          timestamp: Date.now(),
        }
        localStorage.setItem('notificationBirthdayForm', JSON.stringify(draft))
        form.setFieldsValue(draft)
        setNotification((prev) => ({
          ...prev,
          locale,
          timezone: initialTz,
        }))
        setInitialDestination([{}])
      }

      setIsLoadingNotification(false)
    }
  }, [match.params.id])

  useEffect(() => {
    if (isLoading) {
      fetchData()
    }
  }, [isLoading])

  const handleSetInitialDestinationFromStorage = (notificationFormObject) => {
    const destinations: ISelectedDestination[] | [{}] = []
    const notificationDestinations: IDestination[] = []
    for (const [key] of Object.entries(notificationFormObject)) {
      if (key.includes('destination-') && !isEmpty(notificationFormObject[key])) {
        destinations.push({
          type: notificationFormObject[key].type,
          values: notificationFormObject[key].addresses,
        })
        const isMicrosoft = Boolean(notificationFormObject[key].msGroup)
        let platform = ''
        if (isMicrosoft) {
          platform = 'microsoft'
        } else if (notificationFormObject[key].type === 'Email') {
          platform = 'email'
        } else {
          platform = 'slack'
        }
        notificationDestinations.push({
          platform,
          addresses: notificationFormObject[key].addresses,
          ...(notificationFormObject[key].msGroup && { msGroup: notificationFormObject[key].msGroup }),
          ...(notificationFormObject[key].channelName && { channelName: notificationFormObject[key].channelName }),
        })
      }
    }
    setInitialDestination(destinations.length > 0 ? destinations : [{}])
    setDestinationSelected(notificationDestinations || [])
  }

  const fetchData = async () => {
    try {
      let response
      const abortController = abortControllerRef.current
      if (amIAdmin) {
        response = await getLocationsTeamsAndLabelsShortQuery({
          fetchOptionsOverrides: {
            signal: abortController?.signal,
          },
        })
        if (!abortController?.signal.aborted) {
          setTeams(response.data.getTeamListV2 as IGetTeamsShort[])
        }
      }
      if (amIApprover) {
        setApplyToAllUsers(false)
        response = await getLocationsApproverTeamsAndLabelsQuery({
          variables: { id },
          fetchOptionsOverrides: {
            signal: abortController?.signal,
          },
        })
        if (!abortController?.signal.aborted) {
          setTeams(response.data.getUser.approverToTeams as ITeamShort[])
        }
      }
      if (!isUpadateNotification) {
        getTotalUsers()
      }
      if (response?.data && !abortController?.signal.aborted) {
        setLocations(response.data.getLocationListV2 as IGetLocationsShort[])
        setLabels(response.data.getLabelListV2 as IGetLabelsShort[])
      }
      const usersResponse = await getUsersFilteredByStatusQuery({
        variables: { status: 'ACTIVE' },
        fetchOptionsOverrides: {
          signal: abortController?.signal,
        },
      })
      if (usersResponse.data && !abortController?.signal.aborted) {
        setUsers(usersResponse.data.getUsersFiltered.users)
      }

      if (!abortController?.signal.aborted) {
        setIsLoading(false)
      }
    } catch (err) {
      if (err.fetchError?.name === 'AbortError') {
        // Not an error, we aborted the fetch action
        return
      }
      logger.error('ERROR FETCH DATA', err)
    }
  }

  const fetchNotification = async (id) => {
    try {
      const abortController = abortControllerRef.current
      const response = await getNotificationByIdQuery({
        variables: { id },
        fetchOptionsOverrides: {
          signal: abortController?.signal,
        },
      })
      if (!response.data || response.error) throw response.error
      if (!redirectIfEntityNotFound(response.data.getNotification, FrontendUrls.notifications, history)) return
      const notificationData = response.data.getNotification
      notificationData.destinations.map((destination, index) => {
        notificationData[`destination-${index}`] = {
          type: destination.platform === 'email' ? 'Email' : formatMessage({ id: 'components.selectChannelForm.channel' }),
          addresses: destination.platform === 'email' ? destination.addresses : destination.addresses[0],
          msGroup: destination.platform === 'microsoft' ? destination.msGroup : undefined,
        }
      })

      if (!abortController?.signal.aborted) {
        const notificationDestinations = notificationData.destinations.map((destination) => {
          return {
            type: destination.platform === 'email' ? 'Email' : formatMessage({ id: 'components.selectChannelForm.channel' }),
            values: destination.platform === 'email' ? destination.addresses : destination.addresses[0],
          }
        })
        setInitialDestination(notificationDestinations)
        setDestinationSelected(notificationData.destinations)

        await getTotalUsers(notificationData.locations, notificationData.teams, notificationData.labels)

        setInitialFilterValues(() => {
          const data: ISelected[] = []

          if (notificationData) {
            if (notificationData.locations.length > 0) {
              data.push({ type: 'Locations', values: notificationData.locations })
            }
            if (notificationData.teams.length > 0) {
              data.push({ type: 'Departments', values: notificationData.teams })
            }
            if (notificationData?.labels && notificationData.labels.length > 0) {
              data.push({ type: 'Labels', values: notificationData.labels })
            }
          }
          setFilter({
            labelIds: notificationData.labels,
            locationIds: notificationData.locations,
            teamIds: notificationData.teams,
          })

          if (data.length === 0) {
            return [{}]
          }
          setApplyToAllUsers(false)
          return data
        })

        if (notificationData.frequency === 'WEEKLY') {
          notificationData.currentWeek = notificationData.currentWeek ? 1 : 0
        }

        setNotification(notificationData)
        setIsLoadingNotification(false)
      }
    } catch (err) {
      if (err.fetchError?.name === 'AbortError') {
        // Not an error, we aborted the fetch action
        return
      }
      logger.error('ERROR FETCHING NOTIFICATION', err)
    }
  }

  const getTotalUsers = async (locationIds: string[] = [], teamIds: string[] = [], labelIds: string[] = []) => {
    try {
      const abortController = abortControllerRef.current
      const usersResponse = await getUsersForNotifictionsQuery({
        variables: { locationIds, teamIds, labelIds },
        fetchOptionsOverrides: {
          signal: abortController?.signal,
        },
      })
      if (!usersResponse.data || usersResponse.error) throw usersResponse.error
      if (!abortController?.signal.aborted) {
        setTotalUsers(usersResponse.data.getUsersFiltered.totalUsers)
        setTotalUsersWithBirthdayDate(usersResponse.data.getUsersFiltered.totalUsersWithBirthdayDate)
      }
    } catch (err) {
      if (err.fetchError?.name === 'AbortError') {
        // Not an error, we aborted the fetch action
        return
      }
      throw err
    }
  }

  const backToNotifications = () => {
    history.push(FrontendUrls.notifications)
  }

  const onChangeTime = (time) => {
    setShouldResetFilters(false)
    setShouldResetNotificationDestination(false)
    setNotification((prevValue) => ({
      ...prevValue,
      hour: Number(dayjs(time).format('HH')),
      minute: Number(dayjs(time).format('mm')),
    }))
  }

  const handleLocalStorageDestinationChanges = (newValues: Record<string, unknown>) => {
    if (!isUpadateNotification) {
      localStorage.setItem('notificationBirthdayForm', JSON.stringify(newValues))
    }
    handleSetInitialDestinationFromStorage(newValues)
  }

  const onFinish = async () => {
    setIsApiInProgress(true)
    let response
    try {
      const values = await form.validateFields()

      const destinations: IDestination[] = []
      for (const [key, val] of Object.entries(values)) {
        if (key.includes('destination-')) {
          const value: IDestinationForm = val as IDestinationForm
          const platform: NotificationPlatform = authCompany?.platform as NotificationPlatform
          destinations.push({
            platform: value.type === 'Email' ? 'email' : platform,
            addresses: value.type === 'Email' ? value.addresses : [value.addresses as string],
            msGroup: value.msGroup,
            ...(channelName && value.type !== 'Email' ? { channelName } : {}),
          })
        }
      }

      const data = {
        name: values.name,
        sendOnNonWorkingDays: values.sendOnNonWorkingDays,
        ...(values.managersReminders ? { managersReminders: values.managersReminders } : {}),
        locale: values.locale,
        hour: notification.hour,
        minute: notification.minute,
        timezone: values.timezone,
        destinations,
        enabled: isUpadateNotification ? notification.enabled : true,
        locations: applyToAllUsers ? [] : filter.locationIds,
        teams: applyToAllUsers ? [] : amIApprover ? values.teams : filter.teamIds,
        labels: applyToAllUsers ? [] : filter.labelIds,
      }

      response = await Api.post('/core/event', {
        eventType: isUpadateNotification ? 'NOTIFICATION_BIRTHDAY_UPDATED' : 'NOTIFICATION_BIRTHDAY_CREATED',
        eventGroup: 'NOTIFICATION',
        notificationId: isUpadateNotification ? match.params.id : undefined,
        ...data,
      })

      antdNotification.open({
        key: response.correlationId,
        message: match.params.id
          ? formatMessage({ id: 'notificationsForm.updateInProgress' }, { notificationName: response.name })
          : formatMessage({ id: 'notificationsForm.createInProgress' }, { notificationName: response.name }),
        icon: <LoadingOutlined />,
        duration: 0,
      })
      setActionNotifications([...actionNotifications, response.correlationId])

      if (!isUpadateNotification) {
        if (localStorage.getItem('notificationBirthdayForm')) {
          localStorage.removeItem('notificationBirthdayForm')
        }

        if (localStorage.getItem('notificationBirthdayFormFilters')) {
          localStorage.removeItem('notificationBirthdayFormFilters')
        }
      }

      setIsApiInProgress(false)
      history.push(FrontendUrls.notifications)

      if (!match.params.id) {
        form.resetFields()
      }
    } catch (error) {
      logger.error('ERROR HANDLE SUBMIT', error)
      setIsApiInProgress(false)

      const errorMessage = response?.correlationId
        ? formatMessage({ id: 'error.saveFailed' }, { correlationId: response.correlationId })
        : error.response?.data?.message
          ? error.response?.data?.message
          : error.message
            ? error.message
            : JSON.stringify(error)

      const msg = (errorMessage || '').toLowerCase()

      const checkInvalidEmail = msg.includes('valid email') && msg.includes('destinations[') && msg.includes('.addresses[')

      const prettyMessage = checkInvalidEmail ? formatMessage({ id: 'notificationsForm.invalidEmail' }) : errorMessage

      antdNotification.error({
        message: formatMessage({ id: 'error.saveFailed' }),
        description: prettyMessage,
        duration: 0,
      })
    }
  }

  const handleValuesChange = () => {
    setShouldResetFilters(false)
    setShouldResetNotificationDestination(false)
    const currentNotification = form.getFieldsValue(true) as Record<string, unknown>

    if (!isUpadateNotification) {
      localStorage.setItem('notificationBirthdayForm', JSON.stringify(currentNotification))
    }

    handleSetInitialDestinationFromStorage(currentNotification)
  }

  const handleFilter = (filterArg: IFilter) => {
    setFilter(filterArg)
    setShouldResetFilters(false)
    setShouldResetNotificationDestination(false)
    getTotalUsers(filterArg.locationIds, filterArg.teamIds, filterArg.labelIds)
  }

  const handleResetForm = () => {
    const initialTz = resolveNotificationTimezone(userTimezonePreference)
    const defaultNotification = {
      ...defaultNotificationState,
      locale,
      timezone: initialTz,
      timestamp: Date.now(),
    }
    localStorage.setItem('notificationBirthdayForm', JSON.stringify(defaultNotification))
    form.resetFields()
    form.setFieldsValue(defaultNotification)
    setNotification({
      ...defaultNotificationState,
      locale,
      timezone: initialTz,
    } as INotificationForm)
    setInitialDestination([{}])
    setFilter({
      locationIds: [],
      teamIds: [],
      labelIds: [],
    })
    setInitialFilterValues([{}])
    setShouldResetFilters(true)
    setShouldResetNotificationDestination(true)
    setDestinationSelected([])
  }

  const layout = {
    labelCol: { span: 24 },
    wrapperCol: { span: 24 },
  }

  return (
    <div className="main-content vt-notifications-form-page">
      <PageHeader
        title={isUpadateNotification ? <IntlMessages id="notifications.updateBirthdayNotification" /> : <IntlMessages id="notifications.newBirthdayNotification" />}
        parent={[{ label: <IntlMessages id="sidebar.timeOffUpdates" />, to: FrontendUrls.notifications }]}
      />
      <Text>
        <IntlMessages id="notifications.birthdayNotifications.descriptionInfo" />
      </Text>
      <div className="main-content-body" style={{ marginTop: '40px' }}>
        <div className="form-layout">
          {(match.params.id && isLoadingNotification) || isLoading ? (
            <CircularProgress />
          ) : (
            <>
              {notification.error && <Alert message={<IntlMessages id={notification.error} />} type="error" showIcon style={{ marginBottom: '20px' }} />}
              <Form
                layout="vertical"
                form={form}
                name="birthdayNotificationForm"
                initialValues={notification}
                className="birthday-notification-form"
                onFinish={onFinish}
                scrollToFirstError
                labelWrap
                onValuesChange={handleValuesChange}
                size="large"
                {...layout}
              >
                <>
                  <Row
                    gutter={{
                      xs: 8,
                      sm: 16,
                      md: 24,
                      lg: 32,
                    }}
                  >
                    <Col xxl={8} xl={8} lg={6} md={6} sm={24} xs={24}>
                      <Title level={4}>
                        <IntlMessages id="notifications.birthdayNotifications.generalTitle" />
                      </Title>
                      <Paragraph type="secondary">{<IntlMessages id="notifications.birthdayNotifications.generalDescription" />}</Paragraph>
                    </Col>
                    <Col xxl={16} xl={16} lg={18} md={18} sm={24} xs={24}>
                      <Form.Item
                        name="name"
                        label={<IntlMessages id="notifications.birthdayNotifications.name" />}
                        rules={[{ required: true, message: <IntlMessages id="form.inputRequired" /> }]}
                      >
                        <Input maxLength={100} placeholder={`${formatMessage({ id: 'components.teamForm.pleaseEnterTeamName' })}`} />
                      </Form.Item>
                      <YesNoSwitch
                        tooltip={<IntlMessages id="automationsForm.applyToAllUsersTooltip" />}
                        defaultValue={applyToAllUsers}
                        onChange={() => {
                          setApplyToAllUsers(!applyToAllUsers)
                        }}
                        label={<IntlMessages id="automationsForm.applyToAllUsers" />}
                        disabled={amIApprover && !amIAdmin}
                      />
                      {!applyToAllUsers && amIAdmin && (
                        <>
                          <FilterAdvanced
                            isForm={true}
                            data={{
                              Locations: locations,
                              Departments: teams,
                              Labels: labels,
                            }}
                            onChangeFilter={handleFilter}
                            initialValues={initialFilterValues}
                            showLabels={shouldEnableLabels}
                            toolTipInfo="automationsForm.advancedFilter.tooltipInfo"
                            filterByWidth={140}
                            saveFilters={false}
                            shouldReset={shouldResetFilters}
                          />
                        </>
                      )}
                      {!amIAdmin && (
                        <Form.Item label={<IntlMessages id="calendar.filterByTeam" />} rules={[{ required: true, message: <IntlMessages id="form.inputRequired" /> }]} name="teams">
                          <Select mode="multiple" allowClear style={{ width: '100%' }} placeholder={<IntlMessages id="app.pleaseSelect" />} showSearch filterOption={filterOptions}>
                            {[...teams]
                              .sort((a, b) => a.name.localeCompare(b.name))
                              .map((team) => (
                                <Select.Option value={team.id} key={team.id}>
                                  {team.name}
                                </Select.Option>
                              ))}
                          </Select>
                        </Form.Item>
                      )}
                    </Col>
                  </Row>

                  <Divider />

                  <Row
                    gutter={{
                      xs: 8,
                      sm: 16,
                      md: 24,
                      lg: 32,
                    }}
                  >
                    <Col xxl={8} xl={8} lg={6} md={6} sm={24} xs={24}>
                      <Title level={4}>
                        <IntlMessages id="notifications.birthdayNotifications.scheduleAndDelivery" />
                      </Title>
                      <Paragraph type="secondary">{<IntlMessages id="notifications.birthdayNotifications.scheduleAndDeliveryDescription" />}</Paragraph>
                    </Col>
                    <Col xxl={16} xl={16} lg={18} md={18} sm={24} xs={24}>
                      <Form.Item
                        wrapperCol={{ xs: { span: 24 }, sm: { span: 24 }, md: { span: 24 }, lg: { span: 24 } }}
                        className="wrap-label"
                        label={<IntlMessages id="notifications.birthdayNotifications.sendAt" />}
                        required={true}
                      >
                        <div className="form-inline">
                          <Form.Item style={{ width: 125, paddingRight: 20 }}>
                            <TimePicker
                              minuteStep={15}
                              format={hourFormat === HourFormatEnum.twelve ? 'h:mm A' : 'HH:mm'}
                              onCalendarChange={onChangeTime}
                              value={
                                notification?.hour != null && notification?.minute != null
                                  ? dayjs(`${notification.hour.toString().padStart(2, '0')}:${notification.minute.toString().padStart(2, '0')}`, 'HH:mm')
                                  : null
                              }
                              defaultValue={dayjs('10:00', 'HH:mm')}
                              use12Hours={hourFormat === HourFormatEnum.twelve}
                            />
                          </Form.Item>
                          <Form.Item
                            name="timezone"
                            style={{ width: 'calc(100% - 125px)' }}
                            label={''}
                            rules={[{ required: true, message: <IntlMessages id="form.inputRequired" /> }]}
                          >
                            <Select
                              style={{ width: '100%' }}
                              showSearch
                              filterOption={filterOptions}
                              options={timezone.map((tz) => {
                                return { value: tz.id, label: tz.text }
                              })}
                            />
                          </Form.Item>
                        </div>
                      </Form.Item>
                      <NotificationDestinations
                        platform={authCompany?.platform as NotificationPlatform}
                        setChannelName={setChannelName}
                        form={form}
                        initialDestination={initialDestination}
                        notification={notification}
                        onValueChange={handleLocalStorageDestinationChanges}
                        newDesign={true}
                        shouldReset={shouldResetNotificationDestination}
                        filterByWidth={120}
                      />
                      <Form.Item
                        style={{ marginTop: 20 }}
                        name="locale"
                        label={<IntlMessages id="notificationsForm.notificationLocale" />}
                        rules={[{ required: true, message: <IntlMessages id="form.inputRequired" /> }]}
                      >
                        <Select
                          defaultValue={locale || LocaleEnum.en}
                          options={Object.values(availableLanguages).map((language) => {
                            return { value: language.locale, label: language.name }
                          })}
                        />
                      </Form.Item>
                      <YesNoSwitch
                        name="sendOnNonWorkingDays"
                        required={true}
                        defaultValue={sendOnNonWorkingDays}
                        onChange={() => {
                          setSendOnNonWorkingDays(!sendOnNonWorkingDays)
                        }}
                        label={<IntlMessages id="notificationsForm.sendOnNonWorkingDays" />}
                        additionalInfo={
                          <Text type="secondary">
                            <IntlMessages id="notificationsForm.sendOnNonWorkingDaysInfo" />
                          </Text>
                        }
                      />
                    </Col>
                  </Row>

                  <Divider />

                  <Row
                    gutter={{
                      xs: 8,
                      sm: 16,
                      md: 24,
                      lg: 32,
                    }}
                  >
                    <Col xxl={8} xl={8} lg={6} md={6} sm={24} xs={24}>
                      <Title level={4}>
                        <IntlMessages id="notifications.birthdayNotifications.managerHRReminders" />
                      </Title>
                      <Paragraph type="secondary">
                        <IntlMessages id="notifications.birthdayNotifications.managerHRRemindersDescription" />
                      </Paragraph>
                    </Col>
                    <Col xxl={16} xl={16} lg={18} md={18} sm={24} xs={24}>
                      <Form.Item name="managersReminders" label={<IntlMessages id="notifications.birthdayNotifications.threeDayAdvanceReminder" />}>
                        <Select
                          mode="multiple"
                          filterOption={filterOptions}
                          options={users.map((user) => {
                            return { value: user.id, label: user.name }
                          })}
                        ></Select>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Divider />

                  {destinationSelected && destinationSelected.length > 0 && (
                    <Row
                      gutter={{
                        xs: 8,
                        sm: 16,
                        md: 24,
                        lg: 32,
                      }}
                    >
                      <Col xxl={8} xl={8} lg={6} md={6} sm={24} xs={24}>
                        <Title level={4}>
                          <IntlMessages id="app.preview" />
                        </Title>
                        <Paragraph type="secondary">
                          <IntlMessages id="notifications.birthdayNotifications.previewDescription" />
                        </Paragraph>
                      </Col>
                      <Col xxl={16} xl={16} lg={18} md={18} sm={24} xs={24}>
                        <BirthdaySummaryInfo filters={filter} totalUsers={totalUsers} totalUsersWithBirthdayDate={totalUsersWithBirthdayDate} canImportUsers={role === 'Admin'} />
                        {destinationSelected.map((destination) => (
                          <BirthdayMessagePreview
                            platform={destination.platform}
                            userName={authUserName ?? 'User'}
                            time={`${String(notification.hour).padStart(2, '0')}:${String(notification.minute).padStart(2, '0')}`}
                            channelName={destination.channelName}
                            addresses={destination.addresses}
                            previewLocale={previewLocale}
                            key={destination.platform}
                          />
                        ))}
                      </Col>
                    </Row>
                  )}

                  <Row
                    gutter={{
                      xs: 8,
                      sm: 16,
                      md: 24,
                      lg: 32,
                    }}
                  >
                    <Col xxl={8} xl={8} lg={6} md={6} sm={24} xs={24}></Col>
                    <Col xxl={16} xl={16} lg={18} md={18} sm={24} xs={24}>
                      <Form.Item style={{ textAlign: 'right' }}>
                        {!match.params.id && (
                          <Button type="text" onClick={handleResetForm}>
                            <IntlMessages id="app.reset" />
                          </Button>
                        )}
                        <Button className="button-right" type="default" onClick={backToNotifications}>
                          <IntlMessages id="app.cancel" />
                        </Button>
                        <Button className="button-right" htmlType="submit" type="primary" loading={isApiInProgress}>
                          {isUpadateNotification ? <IntlMessages id="notificationsForm.update" /> : <IntlMessages id="notificationsForm.submit" />}
                        </Button>
                      </Form.Item>
                    </Col>
                  </Row>
                </>
              </Form>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default NotificationsBirthdayFormPage
